import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import AddButtonInTopBar from "../components/AddButtonInTopBar";
import DataGrid from "../components/common/DataGrid";
import PageContent from "../components/pageContent";
import ProductBreadCrumb from "../components/products/ProductBreadCrumb";
import TopBarWithBreadCrumb from "../components/TopBarWithBreadCrumb";
import { FormatString } from "../helpers/stringHelpers";
import { checkCrud } from "../helpers/userRolesHelper";
import HardenerPopup from "../popups/HardenerPopup";
import { TranslationProvider } from "../providers/TranslationContext";
import { addNotification } from "../redux/actions";
import { apiGetDiscountGroupsByType } from "../services/DiscountsService";
import { apiDeleteHardener, apiGetAllHardeners } from "../services/HardenerService";

interface Props {
    contextLevel: string;
}

const entityName = 'hardener'
const translationPrefix = 'Hardeners';
const translationModel = { prefix: translationPrefix };

const HardenersPage = (props: Props) => {
    const { t } = useTranslation(undefined, { keyPrefix: translationPrefix });
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState({ rows: [] });
    const [discountGroups, setDiscontGroups] = useState([]);
    const [popupData, setPopupData] = useState({
        visible: false,
        id: undefined,
    });

    const [canCreate, setCanCreate] = useState(false);
    const [canRead, setCanRead] = useState(false);
    const [canUpdate, setCanUpdate] = useState(false);
    const [canDelete, setCanDelete] = useState(false);

    const userInfo = useSelector((state: any) => state.userInfo);

    const setRoles = () => {
        setCanCreate(checkCrud(entityName, 'create', userInfo));
        setCanRead(checkCrud(entityName, 'read', userInfo));
        setCanUpdate(checkCrud(entityName, 'update', userInfo));
        setCanDelete(checkCrud(entityName, 'delete', userInfo));
    }

    useEffect(() => {
        setRoles();
    }, [userInfo.UserRoles])

    const dispatch = useDispatch();

    useEffect(() => {
        loadData();
    }, []);

    const loadData = () => {
        apiGetAllHardeners().then((responseData) => {
            setData({
                rows: responseData.data,
            });

            setLoading(false);
        });
        apiGetDiscountGroupsByType('tuzidlo').then((responseData) => {
            let arr = responseData.data.map((element) => {
                return {
                    value: element.groupId,
                    label: t(`group${element.groupName}`)//element.groupName
                };
            });
            setDiscontGroups(arr);

            setLoading(false);
        });
    };

    const handleDeleteRow = (row) => {
        apiDeleteHardener(row.id)
            .then((responseData) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteSuccess'), row.code),
                        type: "success",
                    })
                );
                loadData();

                setLoading(false);
            })
            .catch((err: any) => {
                dispatch(
                    addNotification({
                        text: FormatString(t('Popup.DeleteFailure'), row.code),
                        type: "error",
                    })
                );
            });
    };


    return (
        <TranslationProvider value={translationModel}>
            {popupData.visible && (
                <HardenerPopup
                    id={popupData.id}
                    refreshData={() => loadData()}
                    handleCancel={() => {
                        setPopupData({ visible: false, id: undefined });
                    }}
                    discountGroups={discountGroups}
                />
            )}
            <TopBarWithBreadCrumb
                contextLevel={props.contextLevel}
                pageLevel="Tuzidlo"
                rightAreaComponent={
                    <AddButtonInTopBar
                        setPopupData={setPopupData}
                        text={t('CreateText')}
                        enabled={canCreate}
                    />
                }
            />

            <PageContent>
                <>
                    <DataGrid
                        loading={loading}
                        rows={data.rows}
                        showDeleteButton={canDelete}
                        showEditButton={canUpdate}
                        onDeleteRow={handleDeleteRow}
                        handleEditAction={(id: any) => {
                            setPopupData({ visible: true, id: id });
                        }}
                        cols={[
                            {
                                name: t('shortName'),
                                dataField: "shortName",
                                sortable: true,
                                filterable: true,
                                filterType: "text",
                            },
                            {
                                name: t('name'),
                                dataField: "name",
                                isLinkToItem: false,
                                sortable: true,
                                filterable: true,
                                filterType: "text",
                            },
                            {
                                name: t('size'),
                                dataField: "size",
                                sortable: true,
                                filterable: false,
                                filterType: "text",
                                fieldType: "numeric"
                            },
                            {
                                name: t('sizeInLitres'),
                                dataField: "sizeInLitresRoundIn3Decimals",
                                sortable: true,
                                filterable: false,
                                filterType: "text",
                                fieldType: "numeric"
                            },
                            {
                                name: t('density'),
                                dataField: "density",
                                sortable: true,
                                filterable: false,
                                fieldType: "numeric"
                            },
                            {
                                name: t('price'),
                                dataField: "price",
                                sortable: true,
                                filterable: false,
                                filterType: "text",
                                fieldType: "numeric"
                            },
                            {
                                name: t('discountGroupName'),
                                dataField: "discountGroupName",
                                sortable: true,
                                filterable: true,
                                filterType: "text",
                                fieldType: "text"
                            },
                            { name: t('noteInternal'), dataField: "noteInternal" },
                            {
                                name: t('createdOn'),
                                fieldType: "datetime",
                                dataField: "createdOn",
                                sortable: true,
                            },
                            {
                                name: t('modifiedOn'),
                                fieldType: "datetime",
                                dataField: "modifiedOn",
                                sortable: true,
                            },
                        ]}
                    />
                </>
            </PageContent>
        </TranslationProvider>
    );
};

export default HardenersPage;
